import { Component, OnInit, computed, effect, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { OnboardingService } from '@lb/api/mylb/services';
import { MDAlertService, MDIconComponent } from '@melodic-ui/components';
import { AnalyticsEvent, AnalyticsEventName, AnalyticsService } from '@lb/services';

type OnboardingStep = 'welcome' | 'show-username' | 'verify-email' | 'set-password' | 'complete' | 'already-complete' | 'error';
type NullableStep = OnboardingStep | null;

// Each funnel step maps to a distinct event so Clarity can chart step-to-step drop-off.
const STEP_EVENTS: Record<OnboardingStep, AnalyticsEventName> = {
	welcome: AnalyticsEvent.OnboardingWelcomeViewed,
	'show-username': AnalyticsEvent.OnboardingUsernameViewed,
	'verify-email': AnalyticsEvent.OnboardingVerifyViewed,
	'set-password': AnalyticsEvent.OnboardingPasswordViewed,
	complete: AnalyticsEvent.OnboardingCompleted,
	'already-complete': AnalyticsEvent.OnboardingAlreadyComplete,
	error: AnalyticsEvent.OnboardingError
};

@Component({
	selector: 'app-onboarding',
	standalone: true,
	imports: [CommonModule, FormsModule, MDIconComponent],
	templateUrl: './onboarding.component.html',
	styleUrl: './onboarding.component.scss'
})
export class OnboardingComponent implements OnInit {
	private _route = inject(ActivatedRoute);
	private _api = inject(OnboardingService);
	private _alertService = inject(MDAlertService);
	private _analytics = inject(AnalyticsService);

	constructor() {
		// Emit a funnel event whenever the step changes. Covers every transition (including
		// error / already-complete / restart) without touching the individual step.set() calls.
		effect(() => {
			const step = this.step();
			if (step) {
				this._analytics.trackEvent(STEP_EVENTS[step]);
			}
		});
	}

	// ─── State ──────────────────────────────────────────────────────────────

	step = signal<NullableStep>(null);
	errorMessage = signal<string | null>(null);
	isSubmitting = signal(false);

	username = signal<string | null>(null);
	maskedEmail = signal<string | null>(null);
	sessionToken = signal<string | null>(null);
	verifiedToken = signal<string | null>(null);
	verifyAttempts = signal(0);
	canStartOver = signal(false);

	// Form fields
	verificationCode = '';
	password = '';
	showPassword = signal(false);

	// Computed helpers
	firstName = computed(() => {
		// Prefer the explicit firstName claim in the JWT
		const token = this._token;
		if (token) {
			const claim = this._decodeJwtClaim(token, 'firstName');
			if (claim) return claim;
		}
		// Fall back to splitting the username
		const name = this.username();
		if (!name) return null;
		const localPart = name.split('@')[0];
		const segment = localPart.split('.')[0];
		return segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase();
	});

	get passwordMeetsRequirements(): boolean {
		return this.pwMinLength && this.pwHasUpper && this.pwHasLower && this.pwHasNumber && this.pwHasSymbol;
	}

	get pwMinLength(): boolean {
		return this.password.length >= 8;
	}
	get pwHasUpper(): boolean {
		return /[A-Z]/.test(this.password);
	}
	get pwHasLower(): boolean {
		return /[a-z]/.test(this.password);
	}
	get pwHasNumber(): boolean {
		return /\d/.test(this.password);
	}
	get pwHasSymbol(): boolean {
		return /[^A-Za-z0-9]/.test(this.password);
	}

	private static readonly SESSION_KEY = 'ob_token';
	private static readonly SESSION_KEY_MASKED_EMAIL = 'ob_masked_email';
	private static readonly SESSION_KEY_SESSION_TOKEN = 'ob_session_token';
	private static readonly SESSION_KEY_EXPIRY = 'ob_session_expiry';

	private get _token(): string | null {
		return sessionStorage.getItem(OnboardingComponent.SESSION_KEY);
	}

	private set _token(value: string | null) {
		if (value) {
			sessionStorage.setItem(OnboardingComponent.SESSION_KEY, value);
		} else {
			sessionStorage.removeItem(OnboardingComponent.SESSION_KEY);
		}
	}

	private _saveInitiateResult(maskedEmail: string | null, sessionToken: string | null): void {
		if (maskedEmail) sessionStorage.setItem(OnboardingComponent.SESSION_KEY_MASKED_EMAIL, maskedEmail);
		if (sessionToken) sessionStorage.setItem(OnboardingComponent.SESSION_KEY_SESSION_TOKEN, sessionToken);
		// Store expiry as now + 60 minutes
		sessionStorage.setItem(OnboardingComponent.SESSION_KEY_EXPIRY, String(Date.now() + 60 * 60 * 1000));
	}

	private _clearInitiateResult(): void {
		sessionStorage.removeItem(OnboardingComponent.SESSION_KEY_MASKED_EMAIL);
		sessionStorage.removeItem(OnboardingComponent.SESSION_KEY_SESSION_TOKEN);
		sessionStorage.removeItem(OnboardingComponent.SESSION_KEY_EXPIRY);
	}

	private _clearAll(): void {
		this._token = null;
		this._clearInitiateResult();
	}

	// ─── Lifecycle ──────────────────────────────────────────────────────────

	ngOnInit(): void {
		const urlToken = this._route.snapshot.queryParamMap.get('token');

		if (urlToken) {
			// New link visit — clear any cached initiate state so this token
			// starts a fresh flow, then persist and strip from URL.
			this._clearInitiateResult();
			this._token = urlToken;
			window.history.replaceState({}, '', '/onboard');
		}

		if (!this._token) {
			this.step.set('error');
			this.errorMessage.set('No onboarding token found. Please use the link from your welcome email.');
			return;
		}

		// Decode the username from the JWT payload — no API call needed.
		const jwtUsername = this._decodeJwtClaim(this._token, 'unique_name') ?? this._decodeJwtClaim(this._token, 'name');
		if (jwtUsername) {
			this.username.set(jwtUsername);
		}

		// If initiate was already called (code already sent), restore that state
		// and skip straight to show-username — no need to re-send the code.
		const cachedSessionToken = sessionStorage.getItem(OnboardingComponent.SESSION_KEY_SESSION_TOKEN);
		const cachedMaskedEmail = sessionStorage.getItem(OnboardingComponent.SESSION_KEY_MASKED_EMAIL);
		const cachedExpiry = sessionStorage.getItem(OnboardingComponent.SESSION_KEY_EXPIRY);
		const isExpired = !cachedExpiry || Date.now() > Number(cachedExpiry);

		if (cachedSessionToken && cachedMaskedEmail && !isExpired) {
			this.sessionToken.set(cachedSessionToken);
			this.maskedEmail.set(cachedMaskedEmail);
			this.step.set('show-username');
			return;
		}

		// Cached state is expired or missing — clear it and start fresh.
		if (isExpired && cachedSessionToken) {
			this._clearInitiateResult();
		}

		this.step.set('welcome');
	}

	// ─── Step navigation ────────────────────────────────────────────────────

	nextFromWelcome(): void {
		if (!this._token) return;

		this.isSubmitting.set(true);
		this.errorMessage.set(null);

		this._api.initiate({ token: this._token }).subscribe({
			next: (res) => {
				this.isSubmitting.set(false);
				if (res.alreadyComplete) {
					this.step.set('already-complete');
					return;
				}
				// Always prefer the username returned from initiate — it comes directly
				// from Entra (Azure AD) and is the authoritative login identifier.
				if (res.username) {
					this.username.set(res.username);
				}
				this.maskedEmail.set(res.maskedEmail);
				this.sessionToken.set(res.sessionToken);
				this._saveInitiateResult(res.maskedEmail, res.sessionToken);
				this.errorMessage.set(null);
				this.step.set('show-username');
			},
			error: (err: HttpErrorResponse) => {
				this.isSubmitting.set(false);
				// Stay on welcome screen and show inline error so the user can retry
				this.errorMessage.set(
					err.status === 401
						? 'Your onboarding link has expired or is invalid. Please use the link from your welcome email.'
						: 'Something went wrong. Please check your connection and try again.'
				);
			}
		});
	}

	nextFromShowUsername(): void {
		this.step.set('verify-email');
	}

	startOver(): void {
		this._clearInitiateResult();
		this.sessionToken.set(null);
		this.maskedEmail.set(null);
		this.verifiedToken.set(null);
		this.verifyAttempts.set(0);
		this.canStartOver.set(false);
		this.errorMessage.set(null);
		this.verificationCode = '';
		this.password = '';
		this.step.set('welcome');
		this._alertService.addAlert({
			title: 'Session expired',
			message: 'Click Next to request a new verification code.',
			type: 'warning',
			duration: 8000
		});
	}

	// Re-runs initiate with the existing link token and returns the user to
	// verify-email with a fresh code — used when the session expires or is consumed.
	requestNewCode(): void {
		this._restartVerification();
	}

	private _restartVerification(): void {
		if (!this._token) {
			this.step.set('error');
			this.errorMessage.set('No onboarding token found. Please use the link from your welcome email.');
			return;
		}

		this._clearInitiateResult();
		this.sessionToken.set(null);
		this.verifiedToken.set(null);
		this.verifyAttempts.set(0);
		this.canStartOver.set(false);
		this.verificationCode = '';
		this.isSubmitting.set(true);
		this.errorMessage.set(null);

		this._api.initiate({ token: this._token }).subscribe({
			next: (res) => {
				this.isSubmitting.set(false);
				if (res.alreadyComplete) {
					this.step.set('already-complete');
					return;
				}
				if (res.username) this.username.set(res.username);
				this.maskedEmail.set(res.maskedEmail);
				this.sessionToken.set(res.sessionToken);
				this._saveInitiateResult(res.maskedEmail, res.sessionToken);
				this.errorMessage.set(`A new code has been sent to ${res.maskedEmail ?? 'your email'}.`);
				this.step.set('verify-email');
			},
			error: (err: HttpErrorResponse) => {
				this.isSubmitting.set(false);
				this.step.set('error');
				this.errorMessage.set(
					err.status === 401
						? 'Your onboarding link has expired. Please contact support to get a new link sent to you.'
						: 'Something went wrong. Please check your connection and try again.'
				);
			}
		});
	}

	// ─── Step 2: verify email code ──────────────────────────────────────────

	submitVerificationCode(): void {
		const sessionToken = this.sessionToken();
		if (!sessionToken || !this.verificationCode) return;

		this.isSubmitting.set(true);
		this.errorMessage.set(null);

		this._api.verify({ sessionToken, code: this.verificationCode }).subscribe({
			next: (res) => {
				this.verifiedToken.set(res.verifiedToken);
				this.step.set('set-password');
				this.isSubmitting.set(false);
			},
			error: (err: HttpErrorResponse) => {
				this.isSubmitting.set(false);
				const attempts = this.verifyAttempts() + 1;
				this.verifyAttempts.set(attempts);

				if (err.status === 401) {
					const isInvalidCode = err.error?.message?.toLowerCase().includes('invalid verification code');
					if (isInvalidCode) {
						this._analytics.trackEvent(AnalyticsEvent.OnboardingCodeRejected, { attempts });
						if (attempts >= 3) {
							// Too many wrong attempts — automatically get a fresh code
							this._restartVerification();
						} else {
							this.errorMessage.set('That code is incorrect. Please check your email and try again.');
						}
					} else {
						// Session consumed, expired, or not found — automatically re-initiate
						this._restartVerification();
					}
				} else if (err.status === 429) {
					this.canStartOver.set(true);
					this.errorMessage.set('Too many incorrect attempts. Please start over or contact support.');
				} else {
					this.errorMessage.set('Something went wrong. Please check your connection and try again.');
				}
			}
		});
	}

	// ─── Step 3: set password ────────────────────────────────────────────────

	submitPassword(): void {
		const verifiedToken = this.verifiedToken();
		if (!verifiedToken || !this.passwordMeetsRequirements) return;

		this.isSubmitting.set(true);
		this.errorMessage.set(null);

		this._api.complete({ verifiedToken, password: this.password }).subscribe({
			next: () => {
				this._clearAll(); // Clear all sessionStorage — flow is complete
				this.step.set('complete');
				this.isSubmitting.set(false);
			},
			error: (err: HttpErrorResponse) => {
				this.isSubmitting.set(false);
				if (err.status === 401) {
					this.canStartOver.set(true);
					this.errorMessage.set('Your session has expired. Please start over to complete your account setup.');
				} else {
					this.errorMessage.set(
						err.status === 400
							? "Your password doesn't meet the requirements. Please review the hints below and try again."
							: 'Something went wrong while setting your password. Please try again.'
					);
				}
			}
		});
	}

	// ─── Helper ──────────────────────────────────────────────────────────────

	get loginUrl(): string {
		const u = this.username();
		return u ? `/?login_hint=${encodeURIComponent(u)}` : '/';
	}

	togglePasswordVisibility(): void {
		this.showPassword.update(v => !v);
	}

	private _decodeJwtClaim(token: string, claim: string): string | null {
		try {
			const payload = token.split('.')[1];
			const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
			return decoded[claim] ?? null;
		} catch {
			return null;
		}
	}
}
